import type { LucideIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/cn";

export const PageHeader = ({
  title,
  description,
  actions,
  className,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      // No more border-b: the lifted main panel already carries the
      // visual separation. Header just owns its own breathing room
      // and lets the page content flow underneath.
      // Mobile-first: stack the title over the actions (the actions row would
      // otherwise crowd/truncate the title on a narrow screen) with tighter
      // padding; on >=sm go back to the side-by-side desktop header.
      // Equal vertical breathing room — bottom matches top (pt-5/sm:pt-8) so
      // the header sits symmetrically rather than tighter at the bottom.
      "flex flex-col gap-y-3 px-4 pt-5 pb-5",
      "sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-x-6 sm:gap-y-2 sm:px-8 sm:pt-8 sm:pb-8",
      className,
    )}
  >
    <div className="min-w-0 flex-1">
      {/* Bigger, more weighty title — Linear-scale typography. */}
      <h1 className="truncate text-lg font-semibold tracking-tight text-foreground sm:text-xl">
        {title}
      </h1>
      {description && (
        <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
      )}
    </div>
    {actions && (
      <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>
    )}
  </div>
);

export const PageContent = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  // More generous side padding — content needs to breathe more inside
  // the lifted panel for the proportions to feel premium.
  <div className={cn("flex-1 overflow-auto px-4 pb-6 sm:px-8 sm:pb-8", className)} {...props} />
);

export const PageContainer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex h-full flex-col", className)} {...props} />
);

export const EmptyState = ({
  title,
  description,
  icon: Icon,
  action,
}: {
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: React.ReactNode;
}) => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-surface/50 py-16">
    {Icon && <Icon className="h-8 w-8 text-muted-foreground" strokeWidth={1.25} />}
    <div className="text-center">
      <div className="text-sm font-medium text-foreground">{title}</div>
      {description && (
        <div className="mt-1 text-xs text-muted-foreground">{description}</div>
      )}
    </div>
    {action}
  </div>
);
